import {
  getClientPreferenceOptions,
  type ClientPreferenceOption,
  type ClientPreferenceOptions,
} from '@/lib/api/clientPreferenceOptions';
import { MEDICAL_CONDITIONS_API_OPTIONS, MEDICAL_CONDITION_SUBTYPE_OPTIONS } from '@/constants/medical-conditions';

// Bump when the canonical catalog changes so existing users do not retain stale dropdown values.
const STORAGE_KEY = 'cs_client_preference_options_v3';
/** Revalidate at most once per day; otherwise reuse stored options. */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type StoredPreferenceOptions = {
  savedAt: number;
  data: ClientPreferenceOptions;
};

let memoryCache: ClientPreferenceOptions | null = null;
let inFlight: Promise<ClientPreferenceOptions> | null = null;

const fallbackMedicalConditionSubtypes = (): Record<string, ClientPreferenceOption[]> => ({
  diabetes: [...(MEDICAL_CONDITION_SUBTYPE_OPTIONS.diabetes ?? [])],
  thyroid: [...(MEDICAL_CONDITION_SUBTYPE_OPTIONS.thyroid ?? [])],
});

const fallbackMedicalConditions = (): ClientPreferenceOption[] => {
  const subtypes = fallbackMedicalConditionSubtypes();
  return MEDICAL_CONDITIONS_API_OPTIONS.map((condition) => ({
    id: condition.id,
    name: condition.name,
    value: condition.id,
    subtypes: subtypes[condition.id] ?? [],
  }));
};

const emptyOptions = (): ClientPreferenceOptions => ({
  allergies: [],
  foodIntolerances: [],
  dietaryPreferences: [],
  cuisineTypes: [],
  medicalConditions: fallbackMedicalConditions(),
  medicalConditionSubtypes: fallbackMedicalConditionSubtypes(),
});

const withMedicalConditionFallback = (data: ClientPreferenceOptions): ClientPreferenceOptions => {
  const medicalConditions = data.medicalConditions ?? [];
  const medicalConditionSubtypes = data.medicalConditionSubtypes ?? {};
  if (medicalConditions.length > 0) {
    return { ...data, medicalConditions, medicalConditionSubtypes };
  }
  return {
    ...data,
    medicalConditions: fallbackMedicalConditions(),
    medicalConditionSubtypes:
      Object.keys(medicalConditionSubtypes).length > 0
        ? medicalConditionSubtypes
        : fallbackMedicalConditionSubtypes(),
  };
};

const isUsableOptions = (data: ClientPreferenceOptions | null | undefined): data is ClientPreferenceOptions =>
  Boolean(
    data &&
      (data.allergies.length > 0 ||
        data.foodIntolerances.length > 0 ||
        data.dietaryPreferences.length > 0 ||
        data.cuisineTypes.length > 0 ||
        (data.medicalConditions?.length ?? 0) > 0),
  );

const readPersistedCache = (): ClientPreferenceOptions | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPreferenceOptions;
    if (!parsed?.savedAt || !isUsableOptions(parsed.data)) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return withMedicalConditionFallback(parsed.data);
  } catch {
    return null;
  }
};

const writePersistedCache = (data: ClientPreferenceOptions): void => {
  if (typeof window === 'undefined') return;
  try {
    const payload: StoredPreferenceOptions = { savedAt: Date.now(), data };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures; memory cache still works.
  }
};

const readCache = (): ClientPreferenceOptions | null => {
  if (memoryCache && isUsableOptions(memoryCache)) return memoryCache;
  const persisted = readPersistedCache();
  if (persisted) {
    memoryCache = persisted;
    return persisted;
  }
  return null;
};

export const getCachedClientPreferenceOptions = (): ClientPreferenceOptions | null => readCache();

export const getAllergyOptions = (): ClientPreferenceOption[] =>
  readCache()?.allergies ?? [];

export const getFoodIntoleranceOptions = (): ClientPreferenceOption[] =>
  readCache()?.foodIntolerances ?? [];

export const getDietaryPreferenceOptions = (): ClientPreferenceOption[] =>
  readCache()?.dietaryPreferences ?? [];

export const getCuisineTypeOptions = (): ClientPreferenceOption[] =>
  readCache()?.cuisineTypes ?? [];

export const getMedicalConditionOptions = (): ClientPreferenceOption[] =>
  withMedicalConditionFallback(readCache() ?? emptyOptions()).medicalConditions;

export const getMedicalConditionSubtypeOptions = (): Record<string, ClientPreferenceOption[]> =>
  withMedicalConditionFallback(readCache() ?? emptyOptions()).medicalConditionSubtypes;

/** Name/value shape used by most existing UI helpers. */
export const getValidAllergies = (): Array<{ name: string; value: string }> =>
  getAllergyOptions().map(({ name, value }) => ({ name, value }));

export const getValidFoodIntolerances = (): Array<{ name: string; value: string }> =>
  getFoodIntoleranceOptions().map(({ name, value }) => ({ name, value }));

export const getValidPreferences = (): Array<{ name: string; value: string }> =>
  getDietaryPreferenceOptions().map(({ name, value }) => ({ name, value }));

export const getValidCuisineTypes = (): Array<{ name: string; value: string; parentId?: string }> =>
  getCuisineTypeOptions().map(({ name, value, parentId }) => ({
    name,
    value,
    ...(parentId ? { parentId } : {}),
  }));

/** Recipe intolerance tags — same catalog as food intolerances from preference-options. */
export const getRecipeIntoleranceTags = (): Array<{ name: string; value: string }> =>
  getValidFoodIntolerances();

/**
 * Load preference options once and reuse from memory/localStorage.
 * Pass `force: true` to bypass cache (e.g. after admin catalog changes).
 */
export const ensureClientPreferenceOptions = async (
  options?: { force?: boolean },
): Promise<ClientPreferenceOptions> => {
  if (!options?.force) {
    const cached = readCache();
    if (cached) return cached;
  }

  if (inFlight && !options?.force) return inFlight;

  inFlight = (async () => {
    try {
      const data = withMedicalConditionFallback(await getClientPreferenceOptions());
      memoryCache = data;
      if (isUsableOptions(data)) {
        writePersistedCache(data);
      }
      return data;
    } catch {
      const cached = readCache();
      if (cached) return cached;
      const empty = emptyOptions();
      memoryCache = empty;
      return empty;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

export const clearClientPreferenceOptionsCache = (): void => {
  memoryCache = null;
  inFlight = null;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export type { ClientPreferenceOption, ClientPreferenceOptions };
